import { Stack } from '@mui/material';
import {
  GridColDef,
  GridFilterModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { useMemo } from 'react';

import { IAccount, IMeter, IUtility } from 'common/src/types/interfaces';

import DataTable from 'portal/src/components/common/DataTable';
import addNumberColumnProps from 'portal/src/components/common/tables/addNumberColumnProps';
import { isActiveAccount, isActiveMeter } from 'portal/src/utils/checkActive';

import { useBillFormContext } from './BillFormContext';

interface ComponentProps {
  filterModel: GridFilterModel;
  onDoubleClickSelection?: () => void;
  onSelectionChange?: (accountId: string) => void;
  setFilterModel: (filter: GridFilterModel) => void;
  setSortModel: (sort: GridSortModel) => void;
  sortModel: GridSortModel;
}

const SelectAccount = ({
  filterModel,
  onDoubleClickSelection,
  onSelectionChange,
  setFilterModel,
  setSortModel,
  sortModel,
}: ComponentProps) => {
  const { accountId, accounts } = useBillFormContext();

  // get all active, non-sub accounts
  const activeAccounts = useMemo(
    () =>
      accounts.data.filter((a) => isActiveAccount(a as IAccount) && !a.isSub),
    [accounts],
  );

  const gridColumns: GridColDef[] = [
    { field: 'number', headerName: 'Account Number', flex: 1 },
    {
      field: 'utility',
      headerName: 'Utility',
      flex: 1,
      valueGetter: (value: IUtility) => value?.name ?? '-',
    },
    {
      field: 'relatedFacilities',
      headerName: 'Facilities',
      flex: 2,
      valueGetter: (_, row) => {
        const activeMeters = row.meters?.filter((m) =>
          isActiveMeter(m as IMeter),
        );

        return [
          ...new Set(activeMeters?.map((m) => m.parentFacility?.name)),
        ].join(', ');
      },
    },
    {
      field: 'activeMeters',
      flex: 0.6,
      valueGetter: (_, row: IAccount) => {
        const activeMeters = row.meters?.filter((m) =>
          isActiveMeter(m as IMeter),
        );
        return activeMeters?.length ?? 0;
      },
      ...addNumberColumnProps('Active Meters', true),
    },
  ];

  const handleSelectionChange = ({ ids }) => {
    const selection = [...ids][0];
    if (!selection) return;
    onSelectionChange(selection);
  };

  return (
    <Stack direction="row" width="100%">
      <DataTable
        checkboxSelection={false}
        columns={gridColumns}
        dataFetchError={accounts.isError}
        filterModel={filterModel}
        loading={accounts.isFetching}
        onFilterModelChange={setFilterModel}
        onRowDoubleClick={onDoubleClickSelection}
        onRowSelectionModelChange={handleSelectionChange}
        onSortModelChange={setSortModel}
        rows={activeAccounts}
        rowSelectionModel={{
          type: 'include',
          ids: accountId ? new Set([accountId]) : new Set(),
        }}
        sortModel={sortModel}
        toolbarSlotProps={{
          showFilters: true,
        }}
      />
    </Stack>
  );
};

export default SelectAccount;
